import { useCallback } from "react";
import { Button, Select, Radio, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { ModeOfFallbackCreation, NewFallbackStep, ConditionAction } from "@/constants/FallbackEnum";
import { fieldOptions } from "@/constants/Fallback";
import { FilterCondition } from './types';
import { getConditionLabelAsCompressorFailure, handleConditionChange } from './Fallbacks.utils';

const { Option } = Select;

interface RenderFilterStepProps {
    t: (key: string) => string;
    conditions: FilterCondition[];
    compressorOptions: Array<{ label: string; value: string }>;
    shouldShowAddButton: boolean;
    updateConditionHandler: (id: number, key: keyof FilterCondition, val) => void;
    removeConditionHandler: (id: number) => void;
    addConditionHandler: () => void;
    handleApplyFilters: () => void;
    onClose: () => void;
}

export const RenderFilterStep = ({
    t,
    conditions,
    compressorOptions,
    shouldShowAddButton,
    updateConditionHandler,
    removeConditionHandler,
    addConditionHandler,
    handleApplyFilters,
    onClose,
}: RenderFilterStepProps) => {
    const handleConditionChangeWrapper = useCallback((cond: FilterCondition, action: ConditionAction, val) => {
        handleConditionChange(cond, action, val, updateConditionHandler, removeConditionHandler);
    }, [updateConditionHandler, removeConditionHandler]);

    const handleAddConditionClick = useCallback(() => {
        addConditionHandler();
    }, [addConditionHandler]);

    const createChangeHandler = useCallback((cond: FilterCondition, action: ConditionAction) => {
        return (val?) => handleConditionChangeWrapper(cond, action, val);
    }, [handleConditionChangeWrapper]);

    return (
    <>
        <div className="modal-title">{t("newFallbackScenario")}</div>
        <div className="modal-subtitle">
            {t("firstApplyFiltersToDefineYourScenario")}
        </div>

        <div className="filter-content">
            <div className="full-width-space">
                {conditions.map((cond, index) => (
                    <div className="filter-condition-container" key={cond.id}>
                        <div className="filter-condition-content">
                            {index > 0 && (
                                <div className="and-condition-container">
                                    <div className="and-label">{t('AND')}</div>
                                    <Select
                                        className="full-width-select"
                                        placeholder={t('selectScenario')}
                                        value={cond.field || undefined}
                                        onChange={createChangeHandler(cond, ConditionAction.Field)}
                                    >
                                        {fieldOptions
                                            .filter((opt) => !conditions.some((c) => c.field === opt.value))
                                            .map((opt) => (
                                                <Option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </Option>
                                            ))}
                                    </Select>
                                </div>
                            )}

                            {index === 0 && (
                                <Select
                                    className="full-width-select"
                                    placeholder={t('selectScenario')}
                                    value={cond.field || undefined}
                                    onChange={createChangeHandler(cond, ConditionAction.Field)}
                                >
                                    {fieldOptions.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            )}

                            {getConditionLabelAsCompressorFailure(cond) && (
                                <Select
                                    className="compressor-width-select"
                                    mode="multiple"
                                    placeholder={t('selectCompressors')}
                                    value={cond.value as string[]}
                                    onChange={createChangeHandler(cond, ConditionAction.Value)}
                                >
                                    {compressorOptions?.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            )}
                        </div>

                        {conditions.length > 1 && (
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={createChangeHandler(cond, ConditionAction.Remove)}
                            />
                        )}
                    </div>
                ))}

                {shouldShowAddButton && (
                    <div className="add-condition-button" onClick={handleAddConditionClick}>
                        + {t('add')}
                    </div>
                )}
            </div>
        </div>

        <Space className="button-container">
            <Button onClick={onClose}>{t("Cancel")}</Button>
            <Button
                type="primary"
                onClick={handleApplyFilters}
                disabled={!conditions.some((c) => c.field)}
            >
                {t("applyFiltersAndContinue")}
            </Button>
        </Space>
    </>
    );
};

interface RenderTableSelectionStepProps {
    t: (key: string) => string;
    mode: ModeOfFallbackCreation | null;
    scenarioName: string;
    tablesToShow: Array<{ id: string; name: string }>;
    isExistingMode: boolean;
    selectedRangeParentKey: string;
    setMode: (mode: ModeOfFallbackCreation) => void;
    setScenarioName: (name: string) => void;
    setSelectedRangeParentKey: (key: string) => void;
    setCurrentStep: (step: NewFallbackStep) => void;
    handleCreate: () => void;
    onClose: () => void;
}

export const RenderTableSelectionStep = ({
    t,
    mode,
    scenarioName,
    tablesToShow,
    isExistingMode,
    selectedRangeParentKey,
    setMode,
    setScenarioName,
    setSelectedRangeParentKey,
    setCurrentStep,
    handleCreate,
    onClose
}: RenderTableSelectionStepProps) => {
    const handleModeChange = useCallback((e) => {
        setMode(e.target.value);
    }, [setMode]);

    const handleScenarioChange = useCallback((value: string) => {
        const selectedScenario = tablesToShow?.find(scenario => scenario.id === value);
        setScenarioName(selectedScenario?.name || "");
        setSelectedRangeParentKey(selectedScenario?.id || "");
    }, [tablesToShow, setScenarioName, setSelectedRangeParentKey]);

    const handleBackClick = useCallback(() => {
        setCurrentStep(NewFallbackStep.Filters);
    }, [setCurrentStep]);

    return (
    <>
        <div className="modal-title">{t("newFallbackScenario")}</div>
        <div className="modal-subtitle">
            {t("pickOneToBuildOnEitherReuseAnExistingTableOrLetTheEngineSuggestASmartStartingPoint")}
        </div>

        <div className="radio-group-container">
            <Radio.Group
                onChange={handleModeChange}
                value={mode}
            >
                <div className="radio-group-wrapper">
                    <Radio value={ModeOfFallbackCreation.Custom}>{t("createACustomTable")}</Radio>
                    <Radio value={ModeOfFallbackCreation.Existing}>{t("useExistingTable")}</Radio>
                </div>
            </Radio.Group>
        </div>

        {isExistingMode && (
            <>
                <Select
                    className="select-table-number"
                    placeholder={t("selectATableNumber")}
                    value={scenarioName || undefined}
                    onChange={handleScenarioChange}
                >
                    {tablesToShow?.map((scenario) => (
                        <Option key={scenario.name} value={scenario.id}>
                            {scenario.name}
                        </Option>
                    ))}
                </Select>
            </>
        )}

        <Space className="button-container">
            <Button onClick={handleBackClick}>{t("Back")}</Button>
            <Button onClick={onClose}>{t("Cancel")}</Button>
            <Button
                type="primary"
                onClick={handleCreate}
                disabled={!mode || (isExistingMode && !selectedRangeParentKey)}
            >
                {t("Create")}
            </Button>
        </Space>
    </>
    );
};
